import { Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import PageHeader from "../../../components/common/PageHeader";
import { ConfirmationDialog } from "../../../components/confirmation-dialog";
import { DialogV2 } from "../../../components/dialog-v2";
import { DataTable, type DataTableColumn } from "../../../components/table/data-table";
import {
  CategoryEditor,
  type CategoryEditorInitialValues,
  type CategoryEditorSubmitValues,
} from "../components/CategoryEditor";
import { useDeleteCategory } from "../hooks/useDeleteCategory";
import { useGetCategoryList } from "../hooks/useGetCategoryList";
import { useSaveCategory } from "../hooks/useSaveCategory";
import type { Category, CategoryPayload } from "../types/category.types";

const initialEditorValues: CategoryEditorInitialValues = {
  name: "",
  type: "",
  imageUrl: null,
};

const editorValuesFromCategory = (category: Category): CategoryEditorInitialValues => ({
  name: category.name,
  type: category.type,
  imageUrl: category.icon_url,
});

export function CategoriesPage() {
  const { t } = useTranslation();
  const catalog = useGetCategoryList();
  const saveCategory = useSaveCategory();
  const deleteCategoryAction = useDeleteCategory();
  const [editing, setEditing] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState<Category | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const categoryTypeOptions = useMemo(
    () =>
      Array.from(new Set(catalog.items.map((category) => category.type).filter(Boolean)))
        .sort((first, second) => first.localeCompare(second))
        .map((type) => ({ value: type, label: type })),
    [catalog.items],
  );

  const openCreate = () => {
    setEditing(null);
    setActionError(null);
    setDialogOpen(true);
  };

  const openEdit = (category: Category) => {
    setEditing(category);
    setActionError(null);
    setDialogOpen(true);
  };

  const submit = async (values: CategoryEditorSubmitValues) => {
    if (saveCategory.isLoading) return;
    const payload: CategoryPayload = {
      name: values.name,
      type: values.type,
      image: values.image,
    };
    try {
      setActionError(null);
      await saveCategory.execute(editing?.id, payload);
      setDialogOpen(false);
      await catalog.refetch();
    } catch (requestError) {
      setActionError(
        requestError instanceof Error ? requestError.message : t("categories.save_failed"),
      );
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      setActionError(null);
      await deleteCategoryAction.execute(deleting.id);
      setDeleting(null);
      await catalog.refetch();
    } catch (requestError) {
      setActionError(
        requestError instanceof Error ? requestError.message : t("categories.delete_failed"),
      );
      setDeleting(null);
    }
  };

  const columns: DataTableColumn<Category>[] = [
    {
      key: "icon",
      title: t("categories.icon"),
      minWidth: 90,
      render: (item) =>
        item.icon_url ? (
          <img
            src={item.icon_url}
            alt={item.name}
            className="h-8 w-8 rounded object-contain"
          />
        ) : (
          "—"
        ),
    },
    {
      key: "name",
      title: t("categories.name"),
      minWidth: 200,
      render: (item) => <span className="font-medium">{item.name}</span>,
      filter: {
        type: "string",
        value: catalog.keyword,
        onFilter: catalog.setKeyword,
        placeholder: t("categories.search"),
        title: t("categories.filter_by_name"),
        description: t("categories.filter_name_hint"),
        debounceMs: 300,
      },
    },
    {
      key: "type",
      title: t("categories.type"),
      minWidth: 140,
      render: (item) => item.type,
      filter: {
        type: "dropdown",
        value: catalog.type,
        onFilter: catalog.setType,
        options: categoryTypeOptions,
        title: t("categories.filter_by_type"),
        placeholder: t("categories.all_types"),
        clearLabel: t("common.clear"),
        applyLabel: t("common.apply"),
      },
      sort: {
        direction: catalog.sortKey === "type" ? (catalog.sortDirection ?? null) : null,
        onSort: (direction) => catalog.setSort("type", direction),
        title: t("categories.type"),
      },
    },
    {
      key: "actions",
      title: t("categories.actions"),
      align: "right",
      minWidth: 140,
      render: (item) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openEdit(item)}
            aria-label={`${t("categories.edit")} ${item.name}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleting(item)}
            aria-label={`${t("categories.delete")} ${item.name}`}
          >
            <Trash2 className="h-4 w-4 text-[#EC5454]" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <PageHeader
        title={t("categories.title")}
        subtitle={t("categories.description")}
        actions={
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t("categories.add")}
          </Button>
        }
      />
      <div className="mb-4 grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-[#DCDCDC] bg-white p-4 shadow-sm">
          <p className="text-sm text-muted-foreground">{t("categories.matching_total")}</p>
          <p className="mt-1 text-3xl font-semibold text-[#254A70]">{catalog.total}</p>
        </div>
        <div className="rounded-xl border border-[#DCDCDC] bg-white p-4 shadow-sm">
          <p className="text-sm text-muted-foreground">{t("categories.current_page_total")}</p>
          <p className="mt-1 text-3xl font-semibold text-[#254A70]">{catalog.items.length}</p>
        </div>
      </div>
      <div className="rounded-xl border border-[#DCDCDC] bg-white p-4 shadow-sm">
        <div className="mb-4 flex justify-end">
          <Button
            variant="outline"
            onClick={() => void catalog.refetch()}
          >
            {t("categories.refresh")}
          </Button>
        </div>
        {actionError ? <p className="mb-3 text-sm text-[#EC5454]">{actionError}</p> : null}
        <DataTable
          columns={columns}
          dataSource={catalog.items}
          rowKey="id"
          loading={catalog.isLoading}
          error={catalog.error}
          onRetry={() => void catalog.refetch()}
          emptyLabel={t("categories.empty")}
          pagination={{
            page: catalog.page,
            totalPages: Math.max(1, Math.ceil(catalog.total / catalog.pageSize)),
            onPageChange: catalog.setPage,
          }}
        />
      </div>
      <DialogV2
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t("categories.edit_title") : t("categories.add_title")}
        maxWidth="md"
        actions={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              {t("categories.cancel")}
            </Button>
            <Button
              form="category-form"
              type="submit"
              disabled={saveCategory.isLoading}
            >
              {t("categories.save")}
            </Button>
          </div>
        }
      >
        <CategoryEditor
          formId="category-form"
          isOpen={isDialogOpen}
          initialValues={editing ? editorValuesFromCategory(editing) : initialEditorValues}
          typeOptions={categoryTypeOptions}
          isEditMode={editing !== null}
          onSubmit={submit}
        />
      </DialogV2>
      <ConfirmationDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
        title={t("categories.delete_title")}
        description={deleting ? t("categories.delete_confirmation", { name: deleting.name }) : ""}
        confirmText={t("categories.delete")}
        cancelText={t("categories.cancel")}
        isLoading={deleteCategoryAction.isLoading}
        type="danger"
      />
    </div>
  );
}
